/* eslint-disable max-lines */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { datadogRum } from '@datadog/browser-rum';
import { Honeybadger } from '@honeybadger-io/react';
import Intercom, { shutdown } from '@intercom/messenger-js-sdk';
import { useQueryClient } from '@tanstack/react-query';
import { tippy } from '@tippyjs/react';
import { uniqueId } from 'filestack-js';
import _ from 'lodash';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { requestApi } from 'api/Api';
import { getPatients } from 'api/Patients';

import { socketOff, socketOn } from 'lib/clients/socket';
import {
  clinicalNoteSections,
  appointmentStatuses as defaultAptStatuses,
  defaultPracticeSettings,
  defaultSettings
} from 'lib/constants/constants';
import { useUIContext } from 'lib/context/UIContext/UIContext';
import { ia, io } from 'lib/helpers/utility';
import { findOpenAndCloseClinicHours } from 'lib/helpers/workingHours';
import { useSmartNavigate } from 'lib/hooks/useSmartNavigate';

import adminState from './admin/lib/adminState';
import practiceState from './practice/practiceState';
import connectToSocket from './sockets/connectToSocket';
import state from './state';

export default function Utility() {
  const [user_state, setUserState] = useRecoilState(state.userState);
  const [practice_state] = useRecoilState(practiceState.current_member);
  const [currentPractice, setCurrentPractice] = useRecoilState(practiceState.currentPractice);
  const [admin_state, setAdminState] = useRecoilState(adminState.current_admin);
  const setAppointmentStatuses = useSetRecoilState(practiceState.appointmentStatuses);
  const [, setPermissions] = useRecoilState(state.permissions);
  const [, setOwnerPractices] = useRecoilState(practiceState.ownerPractices);
  const [settings, setSettings] = useRecoilState(state.settings);
  const [hasBeenAuthChecked, setHasBeenAuthChecked] = useRecoilState(state.hasBeenAuthChecked);
  const [ownerSelectedLocation, setOwnerSelectedLocation] = useRecoilState(
    practiceState.ownerSelectedLocation
  );
  const setKiosk = useSetRecoilState(state.kioskState);
  const setAppointmentFilters = useSetRecoilState(practiceState.calendarFilters);
  const setSocketSessionToken = useSetRecoilState(state.socketSessionTokenState);
  const queryClient = useQueryClient();
  const { navigateTo, navigate, locationState } = useSmartNavigate();
  const isSelfScheduling = locationState?.selfSchedulingRedirect;
  const hasStepsCompleted = localStorage.getItem('stepsCompleted');
  const location = useLocation() || {};
  const { device } = useUIContext();
  const socket = useRecoilValue(state.socketState);

  const checkSessionStorage = async () => {
    let sessionClientId = sessionStorage.getItem('sessionClientId');

    if (!sessionClientId) {
      sessionClientId = uniqueId(32);
      sessionStorage.setItem('sessionClientId', sessionClientId);
    }

    return sessionClientId;
  };

  const checkUserAndSessionClientId = async () => {
    const sessionClientId = await checkSessionStorage();
    userChecks(sessionClientId);
  };

  useEffect(() => {
    return () => {
      if (socket?.connected) socket.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!hasBeenAuthChecked.checked) {
      checkUserAndSessionClientId();
    }
    if (ownerSelectedLocation) {
      checkToken();
    }
  }, [hasBeenAuthChecked, ownerSelectedLocation]);

  useEffect(() => {
    try {
      let newLocation = '';
      if (location?.pathname?.includes('/portal')) {
        newLocation = 'portal';
      } else if (location?.pathname?.includes('/myportal')) {
        newLocation = 'myportal';
      } else if (location?.pathname?.includes('/admin')) {
        newLocation = 'admin';
      }
      document.documentElement.className = newLocation;
    } catch (error) {
      console.error(error);
    }
  }, [location]);

  useEffect(() => {
    if (!location?.pathname.includes('book') && !isSelfScheduling && hasStepsCompleted) {
      clearAppointmentBook();
    }
  }, [location, isSelfScheduling, hasStepsCompleted]);

  useEffect(() => {
    getPermissions();
  }, [user_state?.id, practice_state, admin_state]);

  useEffect(() => {
    const path = location?.pathname.split(/\//g);
    if (path && path[1] !== 'admin') {
      getSettings();
    }
  }, [user_state?.id, practice_state]);

  const { pathname } = useLocation();

  useEffect(() => {
    pathname.includes('login') && queryClient.invalidateQueries();
  }, [pathname]);

  const clearAppointmentBook = async () => {
    await requestApi({
      url: '/api/patient/appointment/self_schedule',
      params: { type: 'deleteCookie' },
      navigate
    });
  };

  useEffect(() => {
    socketOn('permission-update', (data) => {
      const permissions = data.permissions;
      setPermissions({ ...permissions, loaded: true });
      const navIntent = sessionStorage.getItem('navIntent');
      if (navIntent !== '/checkin') {
        setUserState((prev) => ({ ...prev, authenticated: false, logged_in: false }));
        setHasBeenAuthChecked((prev) => ({ ...prev, checked: false }));
      } else {
        sessionStorage.setItem('permissionUpdatePending', '1');
      }
    });

    return () => {
      socketOff('permission-update');
    };
  }, []);

  useEffect(() => {
    const pending = sessionStorage.getItem('permissionUpdatePending');
    if (!pending) return;

    const navIntent = sessionStorage.getItem('navIntent');
    const isInIntendedPath = location.pathname.startsWith(navIntent);

    if (isInIntendedPath) {
      setUserState((prev) => ({ ...prev, authenticated: false, logged_in: false }));
      setHasBeenAuthChecked((prev) => ({ ...prev, checked: false, isImpersonate: false }));
      sessionStorage.removeItem('navIntent');
      sessionStorage.removeItem('permissionUpdatePending');
      window.location.reload();
    }
  }, [location.pathname]);

  const userChecks = async (sessionClientId) => {
    if (hasBeenAuthChecked.isImpersonate) {
      await getAdminData();
      setHasBeenAuthChecked(() => ({ isImpersonate: false, checked: true }));
      return;
    }

    // get current path
    const path = location?.pathname.split(/\//g);
    // check if user is logged in
    if (user_state.logged_in || practice_state.logged_in || admin_state.logged_in) {
      setHasBeenAuthChecked((prev) => ({ ...prev, checked: true }));
      return;
    }
    // check if the path is in the ignored list or ignored sub-paths
    if (
      ['ia'].includes(location?.pathname) ||
      [
        'password',
        'invoice',
        'ia',
        'contact',
        'book',
        'terms',
        'rwt-plan',
        'mfa-description',
        'privacy',
        'activate',
        'kiosk',
        'checkin',
        'start-checkin',
        'icons',
        'instant-packet',
        'select-practice',
        'unsubscribe'
      ].includes(path[1]) ||
      ['password', 'ia'].includes(path[2])
    ) {
      setHasBeenAuthChecked((prev) => ({ ...prev, checked: true }));
      return;
      // , 'terminal_integration_test', 'button'
    }
    // check if path is admin or not
    if (path[1]) {
      if (path[1] === 'admin') {
        await getAdminData();
      } else {
        await checkToken(sessionClientId);
        await getPermissions();
        await getSettings();
      }
    }

    if (location.pathname === '/') {
      // await getAdminData();
      await checkToken();
      await getPermissions();
      await getSettings();
    }
    setHasBeenAuthChecked((prev) => ({ ...prev, checked: true }));
    return;
  };

  tippy.setDefaultProps({
    theme: 'light',
    touch: device === 'desktop' || device === 'laptop' ? true : false
  });

  const getAdminData = async () => {
    const data = localStorage.getItem('admin-state');
    const resData = await requestApi({ url: '/api/user/check_token', navigate });
    const userPreferences = await requestApi({
      url: '/api/user/preferences/get',
      navigate,
      params: { name: ['sidebar'] }
    });

    if (resData?.token_valid) {
      setHasBeenAuthChecked(() => ({ isImpersonate: false, checked: true }));
      setSocketSessionToken(resData?.socketSession);
      connectToSocket({ sessionToken: resData?.socketSession });

      const updatedPreferences = io(userPreferences?.preferences)
        ? Object.entries(userPreferences?.preferences).reduce((acc, [key, value]) => {
            acc[key] = value.value;
            return acc;
          }, {})
        : {};

      if (data) {
        setAdminState({
          ...JSON.parse(data),
          logged_in: true,
          preferences: updatedPreferences
        });

        getPermissions();
      }
    } else {
      localStorage.clear();
      navigateTo('/admin/login');
    }
  };

  const checkToken = async (sessionClientId) => {
    try {
      const resData = await requestApi({ url: '/api/user/check_token', navigate });
      const { token_valid, user_data, is_admin, session_token, socketSession, kiosk } = resData;

      if (kiosk) {
        setKiosk({ isKiosk: true, ...kiosk });
      }

      if (token_valid) {
        setSocketSessionToken(socketSession);
        connectToSocket({
          sessionToken: socketSession ?? session_token,
          sessionClientId
        });
        user_data?.display_settings &&
          io(user_data?.display_settings?.appointmentFilters) &&
          setAppointmentFilters(user_data.display_settings.appointmentFilters);

        const displaySettings = _.merge({}, defaultSettings, user_data?.display_settings);
        setUserState({
          ...user_state,
          ...user_data,
          display_settings: displaySettings || defaultSettings,
          authenticated: true,
          logged_in: true
        });

        getPermissions();

        const currPractice = await getCurrentPractice();

        const newUser = {
          id: user_data.id,
          name: `${user_data?.f_name} ${user_data?.l_name}`,
          email: user_data?.email,
          admin: is_admin
        };

        if (currPractice) {
          newUser['practiceId'] = currPractice?.id;
          newUser['practiceName'] = currPractice?.name;
        }

        datadogRum.setUser(newUser);

        // start intercom with fresh user data after getting practice info
        Intercom({
          hide_default_launcher: true,
          custom_launcher_selector: '[data-intercom-trigger="true"]',
          app_id: process.env.INTERCOM_APP_ID,
          email: user_data?.email,
          phone: user_data?.phone,
          user_id: user_data?.user_id,
          created_at: user_data?.created_at,
          avatar: user_data?.profile_photo,
          name: `${user_data?.f_name} ${user_data?.l_name}`,
          practiceName: currPractice?.name || currentPractice?.name,
          practiceID: currPractice?.id || currentPractice?.id,
          profileType: user_data?.role
        });

        if (is_admin) {
          Honeybadger.setContext({
            user_id: user_data?.user_id,
            user_email: user_data?.email,
            admin: true
          });
        } else {
          Honeybadger.setContext({
            user_id: user_data?.user_id,
            user_email: user_data?.email,
            practice_name: currPractice?.name,
            practice_id: currPractice?.id
          });
        }
      } else {
        localStorage.clear();
        navigateTo('/login');
        shutdown(); // stop intercom
      }
    } catch (error) {
      localStorage.clear();
      Honeybadger.notify(`checkToken: ${error}`);
    }
  };

  const getCurrentPractice = async () => {
    try {
      const resData = await requestApi({ url: '/api/practice/current_practice', navigate });
      const { practice, child_practices, selected_practice_id, aptStatusByPracticeRoles } = resData;

      if (practice) {
        const workingHours = findOpenAndCloseClinicHours(practice?.available_times);
        setCurrentPractice({
          ...currentPractice,
          id: practice.id,
          name: practice.name,
          email: practice.email,
          phone: practice.phone,
          address: practice?.practiceAddress?.fullAddress ?? '',
          header_photo: practice.header_photo,
          timezone: practice.timezone,
          display_settings: { ...defaultPracticeSettings, ...practice?.display_settings },
          working_hours: workingHours,
          appointment_types: practice?.appointment_types,
          eligibility_counter: practice?.eligibility_counter,
          care_credit: practice?.care_credit,
          surcharge: practice?.surcharge,
          surcharge_percentage: practice?.surcharge_percentage,
          surcharge_enabled: practice?.surcharge_enabled,
          socket_id: practice?.socket_id,
          no_show_fee: practice.no_show_fee,
          tax_id: practice?.tax_id,
          checkin_steps: practice?.checkin_steps,
          fax: practice?.fax,
          displayName: practice?.displayName,
          elab_orders: !!practice?.labsoft_cust_id,
          childPractices: child_practices,
          isMulti: child_practices?.length > 1,
          profession_group_id: practice?.profession_group_id
        });
        if (ia(child_practices)) {
          setOwnerPractices(child_practices);
        }
        if (selected_practice_id) {
          setOwnerSelectedLocation(selected_practice_id);
        }

        setAppointmentStatuses([
          ...(defaultAptStatuses || []),
          ...(aptStatusByPracticeRoles || [])
        ]);
      }
      return { id: currentPractice?.id, name: currentPractice?.name };
    } catch (error) {
      Honeybadger.notify(`getCurrentPractice: ${error}`);
    }
  };
  const getPermissions = async () => {
    if (
      permissions?.loaded ||
      (!user_state.logged_in &&
        !admin_state.logged_in &&
        user_state.logged_in === admin_state.logged_in)
    ) {
      return;
    }
    const resData = await requestApi({ url: '/api/permissions/get', navigate });
    const permissions = resData?.permissions;
    if (permissions) {
      setPermissions({ ...permissions, loaded: true });
    }
  };

  const getSettings = async () => {
    if (
      settings.loaded ||
      (!user_state.logged_in &&
        !admin_state.logged_in &&
        user_state.logged_in === admin_state.logged_in)
    ) {
      return;
    }
    const resData = await requestApi({ url: '/api/practice/settings/get', navigate });
    const loadedSettings = resData?.settings;
    if (loadedSettings) {
      setSettings({ ...clinicalNoteSections, ...loadedSettings?.clinical_notes });
    }
  };

  return <></>;
}

// TODO: Not used anywhere — safe to delete
export const searchPatients = async (searchTerm, navigate, formatDate) => {
  const patients = await getPatients(navigate, { limit: 25, searchTerm, offset: 0 });
  return patients.patients.map((p) => ({
    customLabel: (
      <div>
        {p?.fullName && <span>{p.fullName}</span>}
        {p.dob && <span className="pl-1 text-xs text-neutral-500">{formatDate(p.dob)}</span>}
      </div>
    ),
    label: p?.fullName,
    value: p?.id
  }));
};
